import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { RevenueTrackerSheetLink } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/**
 * Where the Revenue Tracker's Google Sheets mirror lives, read ONCE per tab.
 *
 * ── WHY A LOCAL READER AND NOT `useTrackerSheetLink` ──────────────────────
 *
 * ⚠️ **`@/lib/use-tracker-sheet` IS HARD-WIRED TO `api.clientDeliverableSheet()`
 * AND ITS MODULE CACHE HOLDS ONE ANSWER FOR THE WHOLE TAB.** Pointing this page
 * at it would frame the CLIENT DELIVERABLES spreadsheet under a Revenue
 * heading — a wrong document, embedded, with nothing failing and nothing on
 * screen to say so. `AccountsDashboardPage` made the same call for the same
 * reason and this is a copy of that decision, not a second guess at it.
 *
 * Parameterising the shared hook would be the tidier answer and is an edit to a
 * file this change does not own. `TrackerSheetPanel` IS reused verbatim,
 * because it takes its two URLs as props and knows nothing about which mirror
 * it is showing.
 *
 * ── EVERY FAILURE IS "NO MIRROR", NEVER AN ERROR STATE ────────────────────
 *
 * 🔴 **`GET /revenue-tracker/sheet` WAS NOT PART OF THE CONTRACT HANDED OVER,
 * SO A 404 IS THE EXPECTED ANSWER TODAY AND IS NOT A FAULT.** It resolves to
 * `NO_SHEET`, the Sheet toggle is not rendered, and the page is the table — the
 * same degradation an unconfigured mirror produces. Nothing red, nothing
 * missing that a reader was promised.
 *
 * A resolved answer is cached for the life of the tab; **a rejected one is
 * not**, so one blip cannot hide the affordance for a whole session.
 *
 * ── IT IS READ ONCE AND NOT POLLED ────────────────────────────────────────
 *
 * The value is derived from environment variables on the server. It cannot
 * change under a session; it changes when somebody redeploys.
 *
 * ⚠️ **`lastSyncedAt` IS THEREFORE ALSO READ ONCE, AND THE FRESHNESS LINE IT
 * FEEDS IS AS OLD AS THE PAGE LOAD.** That is honest for what it is asked to
 * answer — *has this mirror ever run, and roughly when* — and it is why the
 * line always says the cadence beside the timestamp. Polling it would spend a
 * request a minute to watch a ten-minute tick.
 */

/** What every failure, every 404 and every unconfigured box resolves to. */
const NO_SHEET: RevenueTrackerSheetLink = {
  configured: false,
  spreadsheetUrl: null,
  spreadsheetPreviewUrl: null,
};

/** The settled answer, once we have one. Never holds a failure — see above. */
let cached: RevenueTrackerSheetLink | null = null;

/** The request in flight, so simultaneous mounts share one rather than racing. */
let inFlight: Promise<RevenueTrackerSheetLink> | null = null;

function readSheetLink(): Promise<RevenueTrackerSheetLink> {
  if (cached) return Promise.resolve(cached);

  inFlight ??= api
    .revenueTrackerSheet()
    .then((response) => {
      /*
       * ⚠️ `?? NO_SHEET` rather than `response.data` directly. A `200` proves
       * acceptance and never the shape of what came back — a proxy answering
       * for the route, or a server whose envelope differs, resolves `undefined`
       * here, and every subscriber reads a field off it inside a render. React
       * escalates a TypeError in render to unmounting the tree, so the page
       * would go blank because a decorative link could not be read.
       */
      cached = response.data ?? NO_SHEET;
      inFlight = null;
      return cached;
    })
    .catch(() => {
      // Not written to `cached`, so the next mount retries. See the header.
      inFlight = null;
      return NO_SHEET;
    });

  return inFlight;
}

/** What a subscriber sees. */
export interface RevenueSheetState {
  loading: boolean;
  /** The EDIT address — what goes in the frame. `null` when there is no mirror. */
  link: string | null;
  /** The read-only address. `null` against a server that does not send one. */
  previewLink: string | null;
  /**
   * ⚠️ **`false` MEANS "THIS SERVER DID NOT SEND THE KEY", WHICH IS NOT THE SAME
   * AS "NEVER SYNCED".** Kept apart because a missing timestamp rendered as a
   * blank reads as *fine*, and that is the exact defect the freshness line
   * exists to close — a working mirror was once reported dead because it had
   * simply not ticked yet, and the screen said nothing either way.
   */
  lastSyncedKnown: boolean;
  /** `null` means the mirror has NEVER RUN. Only meaningful with the flag above. */
  lastSyncedAt: string | null;
}

function stateOf(sheet: RevenueTrackerSheetLink): RevenueSheetState {
  return {
    loading: false,
    link: sheet.spreadsheetUrl ?? null,
    previewLink: sheet.spreadsheetPreviewUrl ?? null,
    /*
     * ⚠️ `'lastSyncedAt' in sheet`, never `!= null`. The whole point of this
     * flag is to tell an ABSENT key from a `null` value, and any truthiness
     * test collapses exactly the two states it exists to separate.
     */
    lastSyncedKnown: 'lastSyncedAt' in sheet,
    lastSyncedAt: sheet.lastSyncedAt ?? null,
  };
}

const PENDING: RevenueSheetState = {
  loading: true,
  link: null,
  previewLink: null,
  lastSyncedKnown: false,
  lastSyncedAt: null,
};

/**
 * ⚠️ **Gate a control on `!loading && link !== null`.** A button that flashes in
 * on the first paint and disappears a moment later reads as a bug in the app,
 * and is worse than one that arrives a beat late.
 */
export function useRevenueTrackerSheet(): RevenueSheetState {
  const { user } = useAuth();
  const signedIn = user !== null;

  /*
   * Seeded from the cache rather than from `PENDING`, so a second mount in the
   * same tab renders the control on its FIRST paint instead of flashing empty.
   */
  const [state, setState] = useState<RevenueSheetState>(() =>
    cached ? stateOf(cached) : PENDING,
  );

  useEffect(() => {
    /*
     * An app-wide reader that fires with no token sends a request that can only
     * 401. This page sits behind `RequireAuth`, so it is belt-and-braces — but
     * it is the kind of guard whose absence is found months later by whoever
     * mounts the hook somewhere new.
     */
    if (!signedIn) return;

    let cancelled = false;
    void readSheetLink().then((sheet) => {
      if (cancelled) return;
      setState(stateOf(sheet));
    });
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  /*
   * Signed out, the answer is DERIVED at render rather than written from the
   * effect: setting it there starts a second render synchronously. `loading` is
   * FALSE — "nobody asked" is a settled answer, and a caller that waits on it
   * would wait for ever.
   */
  return signedIn ? state : { ...PENDING, loading: false };
}
